/**
 * Reine Serialisierung und Vergleichslogik fuer das Dreamteam-Ranking
 * (Kulturprofil, detaillierte Variante). Bewusst getrennt von
 * `components/cultural-fit/dreamteam-ranking`: das Modul dort zieht react-dnd
 * herein, das als ESM ausgeliefert wird und von Jest nicht transformiert wird.
 * Hier liegt alles, was ohne React auskommt, und ist damit direkt testbar.
 */

export const DREAMTEAM_DIMENSIONS = [
  'HEROS_JOURNEY_score_stimulation',
  'HEROS_JOURNEY_score_self_direction',
  'HEROS_JOURNEY_score_benevolence',
  'HEROS_JOURNEY_score_achievement',
  'HEROS_JOURNEY_score_hedonism',
  'HEROS_JOURNEY_score_universalism',
  'HEROS_JOURNEY_score_power',
  'HEROS_JOURNEY_score_conformity',
  'HEROS_JOURNEY_score_security',
  'HEROS_JOURNEY_score_tradition'
]

const I18N_DIMENSIONS = 'create_career.cultural_fit.dreamteam.dimensions.'

// Die beiden Copy-Quellen sind unterschiedlich indiziert. Das System-Item
// DIMENSIONS_CULTURE kennt nur `score_<dimension>`, die i18n-Keys tragen den
// vollen Key inklusive `HEROS_JOURNEY_`. Das Backend strippt an vier Stellen
// genauso, siehe helperControl/text-generation/REQUIREMENTPROFILE/index.js.
const toSystemKey = (key) => key.replace('HEROS_JOURNEY_', '')

/**
 * Backend-Copy (system.dimensions_culture) mit i18n als Fallback. Factory statt
 * Hook, weil der Resolver auch aus Callbacks (moveByButton) aufgerufen wird.
 * Wird von der Read-only-Vorschau mitgenutzt, damit Ranking und Vorschau nicht
 * auseinanderlaufen.
 */
export const createDimensionField =
  ({ dimensions, t }) =>
  (key, field) =>
    dimensions?.[toSystemKey(key)]?.[field] ??
    t(`${I18N_DIMENSIONS}${key}.${field}`)

export const hasLongKeys = (culturalFit) =>
  DREAMTEAM_DIMENSIONS.every((key) => typeof culturalFit[key] === 'number')

export const getLongItems = (culturalFit) =>
  [...DREAMTEAM_DIMENSIONS]
    .map((key) => ({
      key,
      rank: typeof culturalFit[key] === 'number' ? culturalFit[key] : 99
    }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ key }) => key)

export const longItemsToState = (items) =>
  Object.fromEntries(items.map((key, i) => [key, i + 1]))

/**
 * Minimal-Move-Diff gegen eine Baseline-Reihenfolge, liefert nur die
 * tatsaechlich bewegten Werte samt ihrem Rang in der Baseline.
 *
 * Ein positionaler Vergleich waere falsch. Eine Karte von Rang 10 auf Rang 1 zu
 * ziehen schiebt die neun anderen um eine Position nach unten und wuerde damit
 * alle zehn als geaendert melden, obwohl genau eine Aktion stattgefunden hat.
 * Markiert wird deshalb, was NICHT in der laengsten aufsteigenden Teilfolge der
 * Baseline-Raenge liegt. Diese Teilfolge ist die groesste Gruppe, die ihre
 * relative Ordnung behalten hat, alles andere ist bewegt.
 *
 * Beim Tausch direkt benachbarter Raenge existieren zwei maximale Teilfolgen.
 * Markiert wird dann genau einer der beiden Werte, nicht beide. Die Anzahl
 * bleibt korrekt, welchen der beiden es trifft ist willkuerlich.
 *
 * Ein leeres Ergebnis bedeutet immer "Reihenfolge identisch zur Baseline", und
 * ein nicht leeres immer "weicht ab". Das haengt am Fixpunkt-Filter unten und
 * ist erschoepfend fuer alle Permutationen der ersten sechs Raenge geprueft,
 * siehe culture-ranking.test.js.
 *
 * O(n^2) bei n = 10, das bleibt lesbarer als eine Patience-Variante.
 */
export const getMovedFromRanks = (items, baselineRanks) => {
  if (!baselineRanks || !items.length) return {}

  const ranks = items.map((key) => baselineRanks[key])

  // Eine unvollstaendige Baseline ist keine Referenz. Lieber gar keine Marker
  // als ein Chip mit "von Rang undefined".
  if (ranks.some((rank) => typeof rank !== 'number')) return {}

  const lengths = ranks.map(() => 1)
  const previous = ranks.map(() => -1)
  let bestEnd = 0

  for (let i = 0; i < ranks.length; i += 1) {
    for (let j = 0; j < i; j += 1) {
      if (ranks[j] < ranks[i] && lengths[j] + 1 > lengths[i]) {
        lengths[i] = lengths[j] + 1
        previous[i] = j
      }
    }
    if (lengths[i] > lengths[bestEnd]) bestEnd = i
  }

  const stable = new Set()
  for (let i = bestEnd; i !== -1; i = previous[i]) stable.add(items[i])

  return Object.fromEntries(
    items.flatMap((key, index) => {
      if (stable.has(key)) return []

      // Ein Wert, der auf genau demselben Rang steht wie im
      // Unternehmensprofil, hat sich nicht bewegt, auch wenn die Teilfolge ihn
      // nicht mitnimmt. Ohne diesen Filter stuende auf Rang 2 ein Chip
      // "von Rang 2", und das trifft rund jede vierte Reihenfolge.
      const baselineRank = baselineRanks[key]
      if (baselineRank === index + 1) return []

      return [[key, baselineRank]]
    })
  )
}
